/**
 * GET /admin/session — heartbeat the admin portal calls on mount to confirm
 * server-side authorization. Behind [authenticate, requireAdmin] on the API,
 * so a 403 here means the caller is NOT a live ADMIN (regardless of what the
 * client-side type claim says). The portal treats any failure as "not
 * authorized" and bounces the user away.
 */
import { apiJson, apiFetch, ApiError } from '../api';

export type UserStatusValue = 'PENDING' | 'ACTIVE' | 'SUSPENDED';
export type UserTypeValue = 'USER' | 'ADMIN' | 'DEV_USER';

export interface AdminSession {
  userId: string;
  email: string | null;
  type: UserTypeValue | null;
}

export async function getAdminSession(init?: RequestInit): Promise<AdminSession> {
  const res = await apiJson<{ status: number; data: AdminSession }>('/admin/session', init);
  return res.data;
}

/** Overview dashboard payload — mirrors AdminOverview in
 *  api/src/modules/admin/admin.stats.service.ts. All counts exclude
 *  DEV_USER accounts; rates are 0..1. */
export interface AdminOverview {
  generatedAt: string;
  users: {
    total: number;
    pending: number;
    active: number;
    suspended: number;
    disabled: number;
    devUsers: number;
  };
  consent: {
    activeUsers: number;
    marketingOptIns: number;
    marketingOptInRate: number;
    reminderOptIns: number;
    reminderOptInRate: number;
    emailUndeliverable: number;
  };
  play: {
    totalRuns: number;
    uniquePlayers: number;
    byWeek: Array<{ weekNumber: number; runs: number; players: number; topScore: number }>;
  };
  economy: {
    totalEarned: number;
    totalSpent: number;
    outstandingFloat: number;
  };
  shop: {
    topStats: Array<{ statId: string; name: string; owners: number }>;
  };
}

/** Optional ISO-8601 date-range filter for the overview. Omit both bounds for
 *  the all-time (unfiltered) request. Mirrors overviewQuerySchema on the API. */
export interface OverviewParams {
  fromDate?: string;
  toDate?: string;
}

export async function getAdminOverview(
  params: OverviewParams = {},
  init?: RequestInit,
): Promise<AdminOverview> {
  const qs = new URLSearchParams();
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  const q = qs.toString();
  const res = await apiJson<{ status: number; data: AdminOverview }>(
    `/admin/stats/overview${q ? `?${q}` : ''}`,
    init,
  );
  return res.data;
}

/** One day of play in the event's display timezone. Mirrors PlayDay in
 *  api/src/modules/admin/admin.playByDay.service.ts. */
export interface PlayDay {
  date: string; // 'YYYY-MM-DD'
  weekday: string; // 'Fri'
  runs: number;
  /** Distinct players active that day. These are daily actives, so they sum to
   *  MORE than the week's unique-player count in the By-week table. */
  players: number;
}

/** One promotion week's day-by-day series. Mirrors PlayWeekSeries on the API. */
export interface PlayWeekSeries {
  weekNumber: number;
  label: string;
  isFreePlay: boolean;
  startDate: string;
  endDate: string;
  promoStartDate: string;
  promoEndDate: string;
  /** startDate through today. A date missing here hasn't happened yet — draw a
   *  gap, not a zero. */
  days: PlayDay[];
}

export interface PlayByDay {
  generatedAt: string;
  timezone: string;
  today: string;
  weeks: PlayWeekSeries[];
}

/**
 * Day-by-day play volume per week (GHS5-99). Takes no params and is NOT scoped
 * by the overview date range: the graph's windows are the promotion weeks
 * themselves. The whole set arrives at once so view switching and paging are
 * local.
 */
export async function getAdminPlayByDay(init?: RequestInit): Promise<PlayByDay> {
  const res = await apiJson<{ status: number; data: PlayByDay }>('/admin/stats/play-by-day', init);
  return res.data;
}

/** Mirrors EmailMetrics in api/src/modules/admin/admin.emailMetrics.service.ts.
 *  Counts are CloudWatch sums over the window, partitioned by the SFGHS5 send
 *  dimension; rates are 0..1. `metricsAvailable` is false when the CloudWatch
 *  read failed (perms/throttle). */
export interface EmailTypeMetrics {
  value: string;
  label: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  opened: number;
  clicked: number;
  bounceRate: number;
  openRate: number;
  clickRate: number;
}

/** Per-campaign reminder metrics (week-1..5 + test) for the Email tab. */
export interface EpisodeReminderWeekMetrics extends EmailTypeMetrics {
  campaignKey: string;
}

export interface EmailMetrics {
  windowDays: number;
  metricsAvailable: boolean;
  types: EmailTypeMetrics[];
  episodeReminderWeeks: EpisodeReminderWeekMetrics[];
}

export async function getAdminEmailMetrics(
  params: { window?: number; fromDate?: string; toDate?: string } = {},
  init?: RequestInit,
): Promise<EmailMetrics> {
  const qs = new URLSearchParams();
  // An explicit ISO range takes precedence server-side; window is the Email
  // tab's rolling fallback.
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  if (params.window != null) qs.set('window', String(params.window));
  const q = qs.toString();
  const res = await apiJson<{ status: number; data: EmailMetrics }>(
    `/admin/stats/email-metrics${q ? `?${q}` : ''}`,
    init,
  );
  return res.data;
}

// ── Users tab ──────────────────────────────────────────────────────────

/** Mirrors AdminUserRow in api/src/modules/admin/admin.users.service.ts. */
export interface AdminUserRow {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  status: UserStatusValue;
  type: UserTypeValue;
  enabled: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
  disabledReason: string | null;
  disabledAt: string | null;
  disabledByEmail: string | null;
  /** False when a SES bounce/complaint suppressed this address (SNS handler);
   *  sends to them are skipped. Reason is `<type:bounceType:bounceSubType>`,
   *  e.g. "Bounce:Transient:MailboxFull" (rows from before the subtype was
   *  captured read "Bounce:Transient"). */
  canEmail: boolean;
  canEmailReason: string | null;
  canEmailAt: string | null;
  /** Email is eligible for ADMIN promotion (@infinitymarketing.com). */
  promotable: boolean;
}

export interface ListUsersParams {
  search?: string;
  status?: UserStatusValue;
  type?: UserTypeValue;
  limit?: number;
  offset?: number;
}

export interface AdminUserList {
  total: number;
  users: AdminUserRow[];
}

export async function listAdminUsers(params: ListUsersParams, init?: RequestInit): Promise<AdminUserList> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  if (params.type) qs.set('type', params.type);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const res = await apiJson<{ status: number; data: AdminUserList }>(
    `/admin/users?${qs.toString()}`,
    init,
  );
  return res.data;
}

/** Moderation actions. All throw ApiError on failure; the parsed body carries
 *  a `code` (e.g. 'not_promotable', 'self_action') for precise messaging. */
export type UserAction = 'disable' | 'enable' | 'suspend' | 'reinstate' | 'promote' | 'demote';

export async function userAction(
  userId: string,
  action: UserAction,
  reason?: string,
): Promise<void> {
  await apiJson(`/admin/users/${userId}/${action}`, {
    method: 'POST',
    headers: reason ? { 'Content-Type': 'application/json' } : undefined,
    body: reason ? JSON.stringify({ reason }) : undefined,
  });
}

// ── Leaderboard tab ────────────────────────────────────────────────────

export interface AdminStandingRow {
  rank: number;
  userId: string;
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  highScore: number;
  achievedAt: string | null;
}

export interface ExcludedRow {
  userId: string;
  displayName: string;
  email: string;
  highScore: number | null;
  reason: string;
  excludedByEmail: string | null;
  excludedAt: string | null;
}

export interface WeekLeaderboard {
  week: number;
  total: number;
  standings: AdminStandingRow[];
  excluded: ExcludedRow[];
}

export async function getWeekLeaderboard(
  params: { week: number; search?: string; limit?: number; offset?: number },
  init?: RequestInit,
): Promise<WeekLeaderboard> {
  const qs = new URLSearchParams({ week: String(params.week) });
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const res = await apiJson<{ status: number; data: WeekLeaderboard }>(
    `/admin/leaderboard?${qs.toString()}`,
    init,
  );
  return res.data;
}

export async function disqualifyUser(userId: string, weekNumber: number, reason: string): Promise<void> {
  await apiJson(`/admin/leaderboard/${userId}/disqualify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekNumber, reason }),
  });
}

export async function reinstateOnLeaderboard(userId: string, weekNumber: number): Promise<void> {
  await apiJson(`/admin/leaderboard/${userId}/reinstate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekNumber }),
  });
}

// ── Schedule tab ───────────────────────────────────────────────────────

export interface ScheduleConfig {
  preEventActiveStart: string;
  postEventStart: string;
  displayTimezone: string;
}

export interface ScheduleWeek {
  weekNumber: number;
  airAt: string;
  playCutoffAt: string;
}

export interface ScheduleState {
  phase: string;
  currentWeek: number;
  nextTransitionAt: string | null;
  serverTime: string;
}

export interface AdminSchedule {
  config: ScheduleConfig;
  weeks: ScheduleWeek[];
  state: ScheduleState;
}

export async function getAdminSchedule(init?: RequestInit): Promise<AdminSchedule> {
  const res = await apiJson<{ status: number; data: AdminSchedule }>('/admin/schedule', init);
  return res.data;
}

export async function updateAdminSchedule(payload: {
  config: ScheduleConfig;
  weeks: ScheduleWeek[];
}): Promise<AdminSchedule> {
  const res = await apiJson<{ status: number; data: AdminSchedule }>('/admin/schedule', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

// ── Activity tab (audit log) ───────────────────────────────────────────

export interface AuditLogEntry {
  logId: number;
  action: string;
  entity: string;
  entityId: string | null;
  userId: string | null;
  actorEmail: string | null;
  targetEmail: string | null;
  previousData: unknown;
  newData: unknown;
  ipAddress: string | null;
  requestId: string | null;
  createdAt: string | null;
}

export interface ListAuditParams {
  action?: string;
  entity?: string;
  entityId?: string;
  actorUserId?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export interface AuditList {
  total: number;
  entries: AuditLogEntry[];
}

export async function listAuditLog(params: ListAuditParams, init?: RequestInit): Promise<AuditList> {
  const qs = new URLSearchParams();
  if (params.action) qs.set('action', params.action);
  if (params.entity) qs.set('entity', params.entity);
  if (params.entityId) qs.set('entityId', params.entityId);
  if (params.actorUserId) qs.set('actorUserId', params.actorUserId);
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const res = await apiJson<{ status: number; data: AuditList }>(
    `/admin/audit?${qs.toString()}`,
    init,
  );
  return res.data;
}

export async function listAuditActions(init?: RequestInit): Promise<string[]> {
  const res = await apiJson<{ status: number; data: string[] }>('/admin/audit/actions', init);
  return res.data;
}

// ── User detail view ───────────────────────────────────────────────────

export interface UserDetailProfile {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  country: string | null;
  displayName: string;
  phone: string | null;
  status: UserStatusValue;
  type: UserTypeValue;
  enabled: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
  ageConfirmedAt: string | null;
  marketingConsent: boolean | null;
  marketingConsentAt: string | null;
  receivesEpisodeReminders: boolean;
  receivesEpisodeRemindersAt: string | null;
  canEmail: boolean;
  canEmailReason: string | null;
  canEmailAt: string | null;
  disabledReason: string | null;
  disabledAt: string | null;
  disabledByEmail: string | null;
  promotable: boolean;
}

export interface UserDetailBank {
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
}

export interface UserDetailOwnedStat {
  statId: string;
  name: string;
  currentLevel: number;
  maxLevel: number;
  lastUpgradedAt: string | null;
}

export interface UserDetailWeekScore {
  weekNumber: number;
  highScore: number;
  achievedAt: string | null;
  runs: number;
  disqualified: boolean;
  disqualifyReason: string | null;
  disqualifiedAt: string | null;
  disqualifiedByEmail: string | null;
}

export interface UserDetailConsent {
  weekNumber: number;
  rulesVersion: string;
  source: string;
  agreedAt: string | null;
}

export interface UserDetailAuditEntry {
  logId: number;
  action: string;
  actorEmail: string | null;
  createdAt: string | null;
  summary: unknown;
}

export interface UserDetail {
  profile: UserDetailProfile;
  bank: UserDetailBank;
  ownedStats: UserDetailOwnedStat[];
  weekScores: UserDetailWeekScore[];
  consents: UserDetailConsent[];
  auditTail: UserDetailAuditEntry[];
}

export async function getAdminUserDetail(userId: string, init?: RequestInit): Promise<UserDetail> {
  const res = await apiJson<{ status: number; data: UserDetail }>(
    `/admin/users/${encodeURIComponent(userId)}/detail`,
    init,
  );
  return res.data;
}

// ── Per-run inspector + flagging ───────────────────────────────────────
// Mirrors api/src/modules/admin/admin.runs.service.ts. `telemetry` is the
// collect-only anti-cheat object the game build rides in the encrypted payload
// (see web/public/game/src/platformBridge.js buildTelemetryFromScene); the
// server persists it verbatim, so every field is optional here.

export interface UserRunTelemetryCheat {
  inv?: boolean;
  invPerm?: boolean;
  fast?: boolean;
  fastSpd?: number | null;
  enemyPaused?: boolean;
}

export interface UserRunTelemetry {
  v?: number;
  realDurationSec?: number;
  fpsAvg?: number;
  reachedBoss?: boolean;
  bossDefeated?: boolean;
  enteredSwarm?: boolean;
  swarmDurationSec?: number;
  timesHurt?: number;
  damageInstances?: number;
  minLife?: number | null;
  maxLife?: number | null;
  cheat?: UserRunTelemetryCheat;
  cheatEver?: boolean;
  cheatEverAtSec?: number | null;
  cheatSeen?: { inv?: boolean; invPerm?: boolean; fast?: boolean; enemyPaused?: boolean };
  masterSpeed?: number | null;
  maxLifeVal?: number | null;
  swarmEntry?: unknown;
  tiers?: unknown[];
  swarmSamples?: unknown[];
  trimmed?: string;
  error?: string;
  [key: string]: unknown;
}

/** Derived server-side from telemetry — the single definition of "suspicious". */
export interface UserRunCheatSignals {
  usedCheats: boolean;
  invincible: boolean;
  permanentInvincible: boolean;
  fastMove: boolean;
  enemyPaused: boolean;
  noDamage: boolean;
  telemetryMissing: boolean;
  telemetryError: boolean;
}

export interface UserRunBreakdown {
  enemiesDefeated: number | null;
  jewelsCollected: number | null;
  levelReached: number | null;
  bossDefeated: boolean;
  lifeBonus: number | null;
  survivalSeconds: number | null;
}

export interface UserRun {
  recordId: number;
  weekNumber: number;
  sessionId: string | null;
  monotonicSeq: number;
  score: number;
  createdAt: string | null;
  ipAddress: string | null;
  isWeekHighScore: boolean;
  flaggedAt: string | null;
  flaggedReason: string | null;
  flaggedByEmail: string | null;
  breakdown: UserRunBreakdown;
  telemetry: UserRunTelemetry | null;
  cheatSignals: UserRunCheatSignals;
  suspicious: boolean;
  payload: unknown;
}

export interface UserRunsResponse {
  total: number;
  weeks: number[];
  runs: UserRun[];
}

export async function getAdminUserRuns(
  userId: string,
  params: { week?: number; limit?: number; offset?: number } = {},
  init?: RequestInit,
): Promise<UserRunsResponse> {
  const qs = new URLSearchParams();
  if (params.week != null) qs.set('week', String(params.week));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  const res = await apiJson<{ status: number; data: UserRunsResponse }>(
    `/admin/users/${encodeURIComponent(userId)}/runs${q ? `?${q}` : ''}`,
    init,
  );
  return res.data;
}

export async function flagUserRun(userId: string, recordId: number, reason: string): Promise<void> {
  await apiJson(`/admin/users/${encodeURIComponent(userId)}/runs/${recordId}/flag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function unflagUserRun(userId: string, recordId: number): Promise<void> {
  await apiJson(`/admin/users/${encodeURIComponent(userId)}/runs/${recordId}/unflag`, {
    method: 'POST',
  });
}

// ── Catalog tab ────────────────────────────────────────────────────────

export interface StatCatalogStat {
  statId: string;
  name: string;
  description: string;
  type: 'persistent' | 'in_game';
  sortOrder: number;
  maxLevel: number;
  enabled: boolean;
  costs: Array<{ level: number; coinCost: number }>;
}

export interface StatCostsPatch {
  statId: string;
  costs?: Array<{ level: number; coinCost: number }>;
  description?: string;
  enabled?: boolean;
}

export async function getAdminStatCosts(init?: RequestInit): Promise<StatCatalogStat[]> {
  const res = await apiJson<{ status: number; data: StatCatalogStat[] }>(
    '/admin/catalog/stat-costs',
    init,
  );
  return res.data;
}

export async function updateAdminStatCosts(payload: {
  stats: StatCostsPatch[];
}): Promise<StatCatalogStat[]> {
  const res = await apiJson<{ status: number; data: StatCatalogStat[] }>(
    '/admin/catalog/stat-costs',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  return res.data;
}

export interface WeeklyRulesEntry {
  weekNumber: number;
  rulesId: number;
  version: string;
  title: string;
  slug: string;
  effectiveFrom: string;
  published: boolean;
  modifiedAt: string | null;
}

export async function getAdminWeeklyRules(init?: RequestInit): Promise<WeeklyRulesEntry[]> {
  const res = await apiJson<{ status: number; data: WeeklyRulesEntry[] }>(
    '/admin/catalog/weekly-rules',
    init,
  );
  return res.data;
}

export interface WeeklyRulesUpdate {
  title?: string;
  version?: string;
  effectiveFrom?: string;
  published?: boolean;
}

export async function updateAdminWeeklyRule(
  weekNumber: number,
  update: WeeklyRulesUpdate,
): Promise<WeeklyRulesEntry> {
  const res = await apiJson<{ status: number; data: WeeklyRulesEntry }>(
    `/admin/catalog/weekly-rules/${weekNumber}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    },
  );
  return res.data;
}

export interface InGameStatEntry {
  statId: string;
  name: string;
  description: string;
  maxLevel: number;
  sortOrder: number;
  theme: string;
  iconAsset: string;
  enabled: boolean;
  relatedStatId: string | null;
}

export interface InGameStatPatch {
  statId: string;
  name?: string;
  description?: string;
  maxLevel?: number;
  sortOrder?: number;
  theme?: string;
  iconAsset?: string;
  enabled?: boolean;
  relatedStatId?: string | null;
}

export async function getAdminInGameStats(init?: RequestInit): Promise<InGameStatEntry[]> {
  const res = await apiJson<{ status: number; data: InGameStatEntry[] }>(
    '/admin/catalog/in-game-stats',
    init,
  );
  return res.data;
}

export async function updateAdminInGameStats(payload: {
  stats: InGameStatPatch[];
}): Promise<InGameStatEntry[]> {
  const res = await apiJson<{ status: number; data: InGameStatEntry[] }>(
    '/admin/catalog/in-game-stats',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  return res.data;
}

// ── Email Sender tab ───────────────────────────────────────────────────

/** Mirrors the toPayload shape in
 *  api/src/modules/admin/admin.emailCampaigns.service.ts. sendState is the
 *  lifecycle: IDLE → CLAIMED → SENDING → SENT (or FAILED / MISSED). Counts are
 *  from the per-recipient ledger (NOT CloudWatch). */
export interface EmailCampaign {
  campaignKey: string;
  label: string;
  kind: 'test' | 'real';
  enabled: boolean;
  scheduledSendAt: string | null;
  templateKey: string;
  subject: string;
  contentVars: Record<string, unknown>;
  testRecipients: string[];
  sendState: 'IDLE' | 'CLAIMED' | 'SENDING' | 'SENT' | 'FAILED' | 'MISSED';
  startedAt: string | null;
  completedAt: string | null;
  totalRecipients: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  lastError: string | null;
}

export interface EmailCampaignUpdate {
  enabled?: boolean;
  scheduledSendAt?: string | null;
  subject?: string;
  contentVars?: Record<string, unknown>;
  testRecipients?: string[];
}

export async function listEmailCampaigns(init?: RequestInit): Promise<EmailCampaign[]> {
  const res = await apiJson<{ status: number; data: { campaigns: EmailCampaign[] } }>(
    '/admin/email-campaigns',
    init,
  );
  return res.data.campaigns;
}

export async function getEmailCampaignRecipientCount(
  campaignKey: string,
  init?: RequestInit,
): Promise<number> {
  const res = await apiJson<{ status: number; data: { count: number } }>(
    `/admin/email-campaigns/${encodeURIComponent(campaignKey)}/recipient-count`,
    init,
  );
  return res.data.count;
}

export async function updateEmailCampaign(
  campaignKey: string,
  update: EmailCampaignUpdate,
): Promise<EmailCampaign> {
  const res = await apiJson<{ status: number; data: { campaign: EmailCampaign; changed: boolean } }>(
    `/admin/email-campaigns/${encodeURIComponent(campaignKey)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    },
  );
  return res.data.campaign;
}

export async function sendEmailCampaignTestNow(): Promise<EmailCampaign> {
  const res = await apiJson<{ status: number; data: { campaign: EmailCampaign } }>(
    '/admin/email-campaigns/test/send-now',
    { method: 'POST' },
  );
  return res.data.campaign;
}

export async function resetEmailCampaign(campaignKey: string): Promise<EmailCampaign> {
  const res = await apiJson<{ status: number; data: { campaign: EmailCampaign } }>(
    `/admin/email-campaigns/${encodeURIComponent(campaignKey)}/reset`,
    { method: 'POST' },
  );
  return res.data.campaign;
}

// ─── Data Upload tab (MoZeus LiveRamp) ───────────────────────────────
// Mirrors api/src/modules/admin/admin.dataUpload.service.ts.

export interface DataUploadConfig {
  eventCode: string;
  eventEndDate: string | null; // YYYY-MM-DD
  includeRegistrants: boolean;
}

export interface DataUploadConfigUpdate {
  eventCode?: string;
  eventEndDate?: string | null;
  includeRegistrants?: boolean;
}

export interface DataUploadSource {
  sourceKey: string;
  label: string;
  fileName: string | null;
  rowCount: number;
  includedCount: number;
  enabled: boolean;
  uploadedAt: string | null;
}

export interface DataUploadPreview {
  registrantCount: number;
  includeRegistrants: boolean;
  sources: DataUploadSource[];
  estimatedRowsBeforeDedupe: number;
}

export interface DataUploadRun {
  runId: string;
  action: string; // download | download-csv
  status: string; // PENDING | SUCCESS | FAILED
  fileName: string | null;
  eventCode: string | null;
  totalRows: number;
  registrantRows: number;
  sourceRows: number;
  sourceBreakdown: Record<string, number> | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdByUserId: string | null;
  createdByEmail: string | null;
  created: string | null;
}

export async function getDataUploadConfig(init?: RequestInit): Promise<DataUploadConfig> {
  const res = await apiJson<{ status: number; data: DataUploadConfig }>('/admin/data-upload/config', init);
  return res.data;
}

export async function updateDataUploadConfig(update: DataUploadConfigUpdate): Promise<DataUploadConfig> {
  const res = await apiJson<{ status: number; data: DataUploadConfig }>('/admin/data-upload/config', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  });
  return res.data;
}

export async function getDataUploadPreview(init?: RequestInit): Promise<DataUploadPreview> {
  const res = await apiJson<{ status: number; data: DataUploadPreview }>('/admin/data-upload/preview', init);
  return res.data;
}

export async function listDataUploadRuns(
  params: { limit: number; offset: number },
  init?: RequestInit,
): Promise<{ runs: DataUploadRun[]; total: number }> {
  const qs = new URLSearchParams({ limit: String(params.limit), offset: String(params.offset) });
  const res = await apiJson<{ status: number; data: { runs: DataUploadRun[]; total: number } }>(
    `/admin/data-upload/runs?${qs.toString()}`,
    init,
  );
  return res.data;
}

/**
 * Generate the file and download it in the browser. 'download' returns the
 * PGP-encrypted .csv.pgp (the MoZeus deliverable, hand-uploaded to ShareFile);
 * 'download-csv' returns the plain CSV (for verification). Uses apiFetch
 * directly because the response is a binary attachment, not JSON.
 */
export async function downloadDataUploadFile(
  action: 'download' | 'download-csv',
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch('/admin/data-upload/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  // Content-Disposition is only readable same-origin (prod); fall back otherwise.
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? (action === 'download' ? 'StateFarm_Liveramp.csv.pgp' : 'StateFarm_Liveramp.csv');
  return { blob, filename };
}

/** Upload a partner-source CSV (FlowCode/DreamCon, 100 Thieves, …). Multipart —
 *  do NOT set Content-Type; the browser adds the multipart boundary. */
export async function uploadDataUploadSource(params: {
  sourceKey: string;
  label: string;
  file: File;
}): Promise<DataUploadSource> {
  const form = new FormData();
  form.append('sourceKey', params.sourceKey);
  form.append('label', params.label);
  form.append('file', params.file);
  const res = await apiJson<{ status: number; data: DataUploadSource }>('/admin/data-upload/sources', {
    method: 'POST',
    body: form,
  });
  return res.data;
}

export async function setDataUploadSourceEnabled(
  sourceKey: string,
  enabled: boolean,
): Promise<DataUploadSource> {
  const res = await apiJson<{ status: number; data: DataUploadSource }>(
    `/admin/data-upload/sources/${encodeURIComponent(sourceKey)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    },
  );
  return res.data;
}

export async function deleteDataUploadSource(sourceKey: string): Promise<void> {
  await apiJson(`/admin/data-upload/sources/${encodeURIComponent(sourceKey)}`, { method: 'DELETE' });
}

// ── Senders tab (configurable sending domain / transport) ──────────────

export type EmailTransport = 'ses' | 'smtp';

/** Mirrors toPayload in api/src/modules/admin/admin.emailSenders.service.ts.
 *  The SMTP password is never returned — only `hasSmtpPassword`. */
export interface EmailSender {
  senderKey: string;
  label: string;
  transport: EmailTransport;
  fromEmail: string;
  fromName: string;
  replyToEmail: string | null;
  supportEmail: string;
  region: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean | null;
  smtpUser: string | null;
  hasSmtpPassword: boolean;
  enabled: boolean;
  modifiedAt: string | null;
}

export interface EmailRoutes {
  /** emailType → senderKey (e.g. { otp: 'gamerhood', 'episode-reminder': 'gamerhood' }). */
  routes: Record<string, string>;
  /** The routable email types, in display order. */
  types: string[];
}

/** A verified SES identity from the live ListEmailIdentities read. */
export interface SesIdentity {
  identity: string;
  type: string | null;
  verificationStatus: string | null;
  sendingEnabled: boolean | null;
  dkim: string | null;
}

export interface EmailSenderCreate {
  senderKey: string;
  label: string;
  transport: EmailTransport;
  fromEmail: string;
  fromName: string;
  replyToEmail?: string | null;
  supportEmail: string;
  region?: string | null;
  enabled?: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecure?: boolean | null;
  smtpUser?: string | null;
  /** Write-only — sent on create / when changing it; never returned. */
  smtpPassword?: string;
}

export type EmailSenderUpdate = Partial<Omit<EmailSenderCreate, 'senderKey'>>;

export async function listEmailSenders(init?: RequestInit): Promise<EmailSender[]> {
  const res = await apiJson<{ status: number; data: { senders: EmailSender[] } }>(
    '/admin/email-senders',
    init,
  );
  return res.data.senders;
}

export async function getEmailRoutes(init?: RequestInit): Promise<EmailRoutes> {
  const res = await apiJson<{ status: number; data: EmailRoutes }>('/admin/email-senders/routes', init);
  return res.data;
}

export async function listSesIdentities(
  init?: RequestInit,
): Promise<{ identities: SesIdentity[]; region: string }> {
  const res = await apiJson<{ status: number; data: { identities: SesIdentity[]; region: string } }>(
    '/admin/email-senders/ses-identities',
    init,
  );
  return res.data;
}

export async function updateEmailRoutes(
  routes: Record<string, string>,
): Promise<EmailRoutes & { warnings: string[] }> {
  const res = await apiJson<{ status: number; data: EmailRoutes & { warnings: string[] } }>(
    '/admin/email-senders/routes',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(routes),
    },
  );
  return res.data;
}

export async function createEmailSender(
  body: EmailSenderCreate,
): Promise<{ sender: EmailSender; warnings: string[] }> {
  const res = await apiJson<{ status: number; data: { sender: EmailSender; warnings: string[] } }>(
    '/admin/email-senders',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return res.data;
}

export async function updateEmailSender(
  senderKey: string,
  body: EmailSenderUpdate,
): Promise<{ sender: EmailSender; changed: boolean; warnings: string[] }> {
  const res = await apiJson<{
    status: number;
    data: { sender: EmailSender; changed: boolean; warnings: string[] };
  }>(`/admin/email-senders/${encodeURIComponent(senderKey)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.data;
}

export async function deleteEmailSender(senderKey: string): Promise<void> {
  await apiJson(`/admin/email-senders/${encodeURIComponent(senderKey)}`, { method: 'DELETE' });
}

export async function sendEmailSenderTest(
  senderKey: string,
  to: string,
): Promise<{ messageId: string | null }> {
  const res = await apiJson<{ status: number; data: { ok: boolean; messageId: string | null } }>(
    `/admin/email-senders/${encodeURIComponent(senderKey)}/test`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to }),
    },
  );
  return { messageId: res.data.messageId };
}
